import { useCallback, useEffect, useState } from "react";
import { messagerieApi, type ClientMuteState } from "@services/api/messagerie";
import { showNotification } from "context/notification.service";

/**
 * Sourdine complète d'un client (dossier), lue via la discussion client.
 *
 * Partagée par le chat (ChatPanel) et le badge « Client en sourdine » des en-têtes
 * ordre / dossier : un « Réactiver » dans l'un met l'autre à jour aussitôt
 * (événement fenêtre, clé = dossier), sans recharger l'écran.
 */
const EVENT = "carvis:client-mute-changed";

type MuteEventDetail = { state: ClientMuteState };

export const MUTE_REASON_LABELS: Record<string, string> = {
  prise_de_rdv_interne: "« Ne pas notifier le client » coché à la prise de RDV interne",
  reservation_en_ligne_client: "prise de RDV en ligne par le client",
  reservation_non_verifiee: "réservation en ligne non vérifiée (reCAPTCHA impossible dans le navigateur du client)",
};

/** Réservation en ligne prise sans vérification reCAPTCHA : réactiver = valider. */
export const UNVERIFIED_BOOKING_REASON = "reservation_non_verifiee";

export function muteReasonLabel(reason?: string | null): string | null {
  if (!reason) return null;
  return MUTE_REASON_LABELS[reason] || reason;
}

export function formatMuteDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("fr-FR", {
    timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Qui / quand / pourquoi, sur une ligne par information (infobulle). */
export function muteTooltip(state: ClientMuteState | null): string {
  if (!state?.muted) return "";
  const lines = ["Aucun e-mail ni WhatsApp ne part vers ce client (confirmations, rappels, messages)."];
  if (state.muted_by_name) lines.push(`Coupé par ${state.muted_by_name}`);
  const at = formatMuteDate(state.muted_at);
  if (at) lines.push(`Le ${at}`);
  const why = muteReasonLabel(state.muted_reason);
  if (why) lines.push(`Motif : ${why}`);
  if (state.muted_reason === UNVERIFIED_BOOKING_REASON) {
    lines.push("Vérifiez la demande (téléphone, plaque) puis réactivez : la confirmation retenue part alors au client.");
  } else {
    lines.push("Levée automatiquement si le client reprend RDV lui-même en ligne.");
  }
  return lines.join("\n");
}

export function useClientMute(discussionId?: string | null) {
  const [state, setState] = useState<ClientMuteState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!discussionId) { setState(null); return; }
    let cancelled = false;
    messagerieApi.getClientMute(discussionId)
      .then((s) => { if (!cancelled) setState(s); })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [discussionId]);

  // Un autre composant a basculé la sourdine du même dossier.
  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<MuteEventDetail>).detail?.state;
      if (!next?.dossier_id) return;
      setState((prev) => (prev?.dossier_id && prev.dossier_id === next.dossier_id ? next : prev));
    };
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const setMuted = useCallback(async (muted: boolean) => {
    if (!discussionId || busy) return;
    setBusy(true);
    try {
      const next = await messagerieApi.setClientMute(discussionId, muted);
      setState(next);
      window.dispatchEvent(new CustomEvent<MuteEventDetail>(EVENT, { detail: { state: next } }));
      showNotification({
        type: "success",
        message: muted
          ? "Client en sourdine — aucun email ni WhatsApp ne partira vers lui"
          : "Notifications client réactivées",
      });
    } catch {
      showNotification({ type: "error", message: "Impossible de modifier la sourdine du client" });
    } finally {
      setBusy(false);
    }
  }, [discussionId, busy]);

  return { state, busy, setMuted };
}
